import { GetStaticPropsContext } from 'next';

import { Box, Flex, Heading, useToast } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { Footnote } from '~/components/footnote';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { LogosSection } from '~/components/logos-section';
import { Markdown } from '~/components/markdown';
import { ModalButton } from '~/components/modal-button';
import { NotifyMeForm } from '~/components/notify-me-form';
import { SanityImage } from '~/components/sanity-image';
import { ShapesBanner } from '~/components/shapes-banner';
import { StandardHeroSection } from '~/components/standard-hero';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import {
  ScholarshipsTemplate,
  scholarshipsTemplateQuery,
  scholarshipsTemplateSchema,
} from '~/features/page-template/templates/scholarships/model';
import { AvailableScholarshipsSection } from '~/features/scholarships/components/available-scholarships-section';
import { SponsorAScholarshipSection } from '~/features/scholarships/components/sponsor-a-scholarship-section';
import { SuccessStoriesSection } from '~/features/scholarships/components/success-stories-section';
import { getAvailableScholarships } from '~/features/scholarships/controllers/get-available-scholarships';
import { getScholarshipProgramMap } from '~/features/scholarships/controllers/get-scholarship-program-map';
import { AvailableScholarshipList } from '~/features/scholarships/models/available-scholarships';

export default function ScholarshipsPage({
  page,
  scholarshipsTemplate,
  availableScholarships,
  programMap,
}: {
  page: Page;
  scholarshipsTemplate: ScholarshipsTemplate;
  availableScholarships: AvailableScholarshipList;
  programMap: Record<string, { title: string }> | null;
}) {
  const toast = useToast();
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Scholarship} />
      <PageLayout withoutVerticalPadding>
        <StandardHeroSection
          backgroundImageSlot={
            <Flex
              bottom='0px'
              justifyContent='flex-end'
              position='absolute'
              left={{
                base: '0px',
                md: 'calc((100vw - 70rem)/2)',
              }}
              right={{
                base: '0px',
                md: 'calc((100vw - 90rem)/2)',
              }}
              top={{
                base: 'calc(50% + 24px)',
                md: '0px',
              }}
            >
              <Box
                position='relative'
                height={{
                  base: '100%',
                  md: 'auto',
                }}
                width={{
                  base: '100%',
                  md: '800px',
                }}
              >
                <SanityImage
                  alt=''
                  image={scholarshipsTemplate.heroImage}
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'top',
                  }}
                  fill
                  priority
                />
              </Box>
            </Flex>
          }
          contentSlot={
            <>
              <HeadingWithEmphasis
                text={scholarshipsTemplate.pageTitle}
                emphasisProps={{
                  fontWeight: '500',
                }}
                headingProps={{
                  size: 'marketing-5xl',
                  as: 'h1',
                }}
              />
              {scholarshipsTemplate.pageSubtitle && (
                <Heading as='p' color='accent.lime' order='-1' size='marketing-lg'>
                  {scholarshipsTemplate.pageSubtitle}
                </Heading>
              )}
              {scholarshipsTemplate.pageDescription && (
                <Box marginTop='8px'>
                  <Markdown markdownText={scholarshipsTemplate.pageDescription} />
                </Box>
              )}
              <ButtonLink
                buttonProps={{
                  colorScheme: 'sea-foam',
                  width: {
                    base: '100%',
                    md: 'fit-content',
                  },
                  marginTop: '32px',
                  onClick: (e) => {
                    e.preventDefault();
                    trackEvent({
                      name: 'Scholarship Page CTA Clicked',
                      type: 'click',
                      label: 'see_current_scholarships_clicked',
                    });
                    const targetSection = document.getElementById('_available-scholarships-section');
                    if (targetSection) {
                      targetSection.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                      });
                      targetSection.focus({ preventScroll: true });
                    }
                  },
                }}
                linkProps={{
                  href: '#_available-scholarships-section',
                }}
              >
                See Current Scholarships
              </ButtonLink>
            </>
          }
          imageSlot={
            <Box
              flex={{
                base: '1 0 304px',
                md: '1 0 200px',
              }}
            />
          }
        />
        <LogosSection
          companies={scholarshipsTemplate.companySponsors}
          title='Our Scholarship Sponsors'
          containerProps={{
            backgroundColor: 'gray.100',
            color: 'black',
          }}
          logoContainerProps={{
            flex: {
              md: '0 1 850px',
            },
            gap: '24px',
            justifyContent: {
              base: 'flex-start',
              md: 'space-evenly',
            },
          }}
        />
        <SponsorAScholarshipSection
          communityBenefits={scholarshipsTemplate.communityBenefits}
          description={scholarshipsTemplate.sponsorAScholarshipTitleText.text}
          organizationBenefits={scholarshipsTemplate.organizationBenefits}
          title={scholarshipsTemplate.sponsorAScholarshipTitleText.title}
          video={scholarshipsTemplate.video}
        />
        <SuccessStoriesSection
          description={scholarshipsTemplate.successStoriesTitleText.text}
          people={scholarshipsTemplate.successStoriesPeople}
          stats={scholarshipsTemplate.successStoriesStats}
          title={scholarshipsTemplate.successStoriesTitleText.title}
        />
        <AvailableScholarshipsSection
          notifyMeForm={scholarshipsTemplate.notifyMeForm}
          programMap={programMap}
          scholarships={availableScholarships}
          title='Available scholarships'
        />
        <ShapesBanner
          title={scholarshipsTemplate.footerTitle}
          buttonSlot={
            <ModalButton
              buttonProps={{
                size: 'icon-right',
                variant: 'solid',
                colorScheme: 'sea-foam',
                rightIcon: <ArrowRightIcon height='24px' width='24px' />,
                width: {
                  base: '100%',
                  md: 'fit-content',
                },
                onClick: () =>
                  trackEvent({
                    name: 'Scholarship Page CTA Clicked',
                    type: 'click',
                    label: 'notify_me_clicked',
                  }),
              }}
              modalProps={{
                title: 'Get notified for future scholarship opportunities.',
                size: '3xl',
                renderModalContent: ({ onClose }) => (
                  <NotifyMeForm
                    templateForm={scholarshipsTemplate.notifyMeForm}
                    onCancelClick={onClose}
                    onError={() =>
                      toast({
                        variant: 'error',
                        title: 'Oops. Something went wrong. Please try again.',
                      })
                    }
                    onSubmit={() => {
                      toast({
                        title: 'Thank you for your interest!',
                        variant: 'success',
                      });
                      onClose();
                    }}
                  />
                ),
              }}
            >
              Notify Me
            </ModalButton>
          }
        />
        <Footnote text={scholarshipsTemplate.footnote} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext<{ slug: string }>) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, scholarshipsTemplate, availableScholarships] = await Promise.all([
    getPage('/scholarships'),
    getPageTemplate<ScholarshipsTemplate>({
      templateName: 'Scholarships Page',
      templateQuery: scholarshipsTemplateQuery,
      templateZodSchema: scholarshipsTemplateSchema,
    }),
    getAvailableScholarships(),
  ]);

  const programKeys = availableScholarships.flatMap(
    (scholarship) => scholarship.programsOffered?.map(({ key }) => key) || []
  );

  return {
    props: {
      page,
      scholarshipsTemplate,
      availableScholarships,
      programMap: programKeys.length ? await getScholarshipProgramMap([...new Set(programKeys)], 'en-us') : null,
    },
    revalidate: 60 * 60 * 12, // Note: revalidate every 12 hours
  };
}
